import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useParams } from 'wouter';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { format, parseISO } from 'date-fns';
import {
  CalendarCheck,
  CalendarClock,
  Clock,
  Hourglass,
  Loader2,
  Lock,
  LogOut,
  Phone,
  Users,
  XCircle,
} from 'lucide-react';

import {
  customerBookingsService,
  type CustomerBooking,
  type CustomerBookingGroup,
  type CustomerWaitlistEntry,
} from '@/services/api/customer-bookings.service';
import { formatCurrency, type Currency } from '@/utils/currency';
import { getDateLocale } from '@/utils/dateLocale';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { MagicLinkRequestForm } from '@/components/customer/MagicLinkRequestForm';
import { CustomerPageShell } from '@/components/customer/CustomerPageShell';
import { cn } from '@/lib/utils';

/*
 * The customer's booking list.
 *
 * This was a data table. A table is for scanning and comparing many rows; a
 * customer has one to five bookings and wants to act on one of them, so each
 * entry is a card instead. The table also stretched full-bleed with a mostly
 * empty service column, and below `sm` it kept its When/Status headers while
 * re-rendering the service underneath the date, so the headers stopped
 * describing what sat under them.
 */

const STATUS_TONES: Record<string, BadgeTone> = {
  confirmed: 'success',
  pending: 'warn',
  cancelled: 'danger',
  completed: 'neutral',
  no_show: 'neutral',
};

type EntryAction = {
  kind: 'reschedule' | 'cancel' | 'waitlist';
  href: string;
};

type EntryRow = {
  key: string;
  start: Date | null;
  end: Date | null;
  serviceName: string;
  organizationName: string;
  organizationPhone: string | null;
  providerName: string | null;
  group: CustomerBookingGroup | null;
  formattedPrice: string | null;
  note: string | null;
  statusLabel: string;
  statusTone: BadgeTone;
  statusIcon: 'waitlist' | null;
  actions: EntryAction[];
  /** Upcoming, but the business's notice window has closed it to changes. */
  locked: boolean;
  /** Past or cancelled — rendered quieter, never actionable. */
  muted: boolean;
};

function formatPrice(price: number | null, currency: string, hidePrice: boolean): string | null {
  if (hidePrice || price == null || price <= 0) return null;
  return formatCurrency(price, currency as Currency);
}

function bookingToRow(booking: CustomerBooking, t: TFunction, upcoming: boolean): EntryRow {
  const actions: EntryAction[] = [];
  if (booking.can_reschedule) {
    actions.push({ kind: 'reschedule', href: `/guest/bookings/${booking.guest_token}/reschedule` });
  }
  if (booking.can_cancel) {
    actions.push({ kind: 'cancel', href: `/guest/bookings/${booking.guest_token}/cancel` });
  }

  return {
    key: `booking-${booking.id}`,
    start: booking.start_time ? parseISO(booking.start_time) : null,
    end: booking.end_time ? parseISO(booking.end_time) : null,
    serviceName: booking.service.name ?? t('list.unknownService'),
    organizationName: booking.organization.name,
    organizationPhone: booking.organization.phone,
    providerName: booking.provider.display_name,
    group: booking.group,
    formattedPrice: formatPrice(
      booking.service.price,
      booking.organization.currency,
      booking.hide_price,
    ),
    note:
      booking.status === 'cancelled' && booking.cancellation_reason
        ? t('list.cancelledReason', { reason: booking.cancellation_reason })
        : null,
    statusLabel: t(`status.${booking.status}`, { defaultValue: booking.status }),
    statusTone: STATUS_TONES[booking.status] ?? 'neutral',
    statusIcon: null,
    actions,
    // Only worth explaining while the appointment is still ahead of them. A
    // past booking having no actions needs no justification.
    locked: upcoming && actions.length === 0 && booking.status !== 'cancelled',
    muted: !upcoming || booking.status === 'cancelled',
  };
}

function waitlistToRow(entry: CustomerWaitlistEntry, t: TFunction): EntryRow {
  return {
    key: `waitlist-${entry.id}`,
    start: entry.start_time ? parseISO(entry.start_time) : null,
    end: entry.end_time ? parseISO(entry.end_time) : null,
    serviceName: entry.service.name ?? t('list.unknownService'),
    organizationName: entry.organization.name,
    organizationPhone: entry.organization.phone,
    providerName: entry.provider.display_name,
    // A waitlist entry only ever sits on a group class, and the class is full
    // by definition — hence booked === capacity.
    group: { capacity: entry.capacity, booked: entry.capacity, spots_remaining: 0 },
    formattedPrice: formatPrice(entry.service.price, entry.organization.currency, entry.hide_price),
    note: null,
    statusLabel: entry.position
      ? t('list.waitlistPosition', { position: entry.position })
      : t('list.waitlistWaiting'),
    statusTone: 'info',
    statusIcon: 'waitlist',
    actions: [{ kind: 'waitlist', href: `/guest/waitlist/${entry.guest_token}` }],
    locked: false,
    muted: false,
  };
}

/** Big date block at the head of a card. The next appointment fills in brand. */
function DateBlock({ start, emphasis }: { start: Date | null; emphasis: boolean }) {
  const locale = getDateLocale();

  if (!start) {
    return (
      <div className="flex w-16 flex-shrink-0 items-center justify-center rounded-lg bg-surface-2 py-3 text-soft">
        —
      </div>
    );
  }

  return (
    <div
      className={cn(
        'w-16 flex-shrink-0 rounded-lg py-2.5 text-center',
        emphasis ? 'bg-brand-500 text-white' : 'bg-surface-2 text-ink',
      )}
    >
      <div className="text-mono-eyebrow uppercase opacity-85">
        {format(start, 'EEE', { locale })}
      </div>
      <div className="font-display text-[26px] font-bold leading-tight tracking-tight">
        {format(start, 'd', { locale })}
      </div>
      <div className="text-caption opacity-85">{format(start, 'MMM', { locale })}</div>
    </div>
  );
}

function ActionButton({ action }: { action: EntryAction }) {
  const { t } = useTranslation('myBookings');

  const config = {
    reschedule: { label: t('list.reschedule'), Icon: CalendarClock, variant: 'outline' as const },
    cancel: { label: t('list.cancel'), Icon: XCircle, variant: 'outline-danger' as const },
    waitlist: { label: t('list.waitlistManage'), Icon: Hourglass, variant: 'outline' as const },
  }[action.kind];

  const { label, Icon, variant } = config;

  return (
    <Button asChild variant={variant} size="lg" className="h-11 flex-grow sm:flex-grow-0">
      <a href={action.href}>
        <Icon className="h-4 w-4" aria-hidden="true" />
        {label}
      </a>
    </Button>
  );
}

function BookingCard({ row, emphasis }: { row: EntryRow; emphasis: boolean }) {
  const { t } = useTranslation('myBookings');
  const locale = getDateLocale();

  const timeRange = row.start
    ? `${format(row.start, 'HH:mm', { locale })}${row.end ? ` – ${format(row.end, 'HH:mm', { locale })}` : ''}`
    : null;

  return (
    <Card className="overflow-hidden">
      <div className={cn('flex gap-4 p-4 sm:p-[18px]', row.muted && 'opacity-75')}>
        <DateBlock start={row.start} emphasis={emphasis && !row.muted} />

        <div className="flex min-w-0 flex-grow flex-col gap-1">
          {timeRange && (
            <div className="flex items-center gap-1.5 text-body-sm font-semibold text-ink">
              <Clock
                className={cn('h-3.5 w-3.5', emphasis && !row.muted ? 'text-brand-500' : 'text-text-muted')}
                aria-hidden="true"
              />
              {timeRange}
            </div>
          )}

          <h3 className="text-h3 text-ink">{row.serviceName}</h3>

          <p className="text-body-sm text-soft">
            {row.organizationName}
            {row.providerName && ` · ${row.providerName}`}
          </p>

          {row.group && (
            <p className="flex items-center gap-1.5 text-body-xs text-text-muted">
              <Users className="h-3.5 w-3.5" aria-hidden="true" />
              {t('list.groupSpots', {
                booked: row.group.booked,
                capacity: row.group.capacity,
              })}
            </p>
          )}

          {row.note && <p className="text-body-xs text-text-muted">{row.note}</p>}
        </div>

        <div className="flex flex-shrink-0 flex-col items-end gap-2">
          {row.formattedPrice && (
            <span className="font-display text-[15px] font-semibold text-ink">
              {row.formattedPrice}
            </span>
          )}
          <Badge tone={row.statusTone}>
            {row.statusIcon === 'waitlist' && <Hourglass className="mr-1 h-3 w-3" aria-hidden="true" />}
            {row.statusLabel}
          </Badge>
        </div>
      </div>

      {/*
        The old table rendered a bare em dash here, which told the customer no
        and nothing else. The notice window itself is not in the payload, so the
        copy stays general and hands them the business instead of a dead end.
      */}
      {row.locked && (
        <div className="flex items-start gap-2.5 border-t border-line bg-surface-2 px-4 py-3 sm:px-[18px]">
          <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-soft" aria-hidden="true" />
          <p className="text-body-xs text-soft">
            {t('list.changesClosed', { business: row.organizationName })}{' '}
            {row.organizationPhone && (
              <a
                href={`tel:${row.organizationPhone}`}
                className="inline-flex items-center gap-1 font-medium text-brand-500 hover:text-brand-700"
              >
                <Phone className="h-3 w-3" aria-hidden="true" />
                {row.organizationPhone}
              </a>
            )}
          </p>
        </div>
      )}

      {row.actions.length > 0 && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line px-4 py-3 sm:px-[18px]">
          {row.actions.map((action) => (
            <ActionButton key={action.kind} action={action} />
          ))}
        </div>
      )}
    </Card>
  );
}

function EntriesList({ rows, emphasiseFirst }: { rows: EntryRow[]; emphasiseFirst?: boolean }) {
  return (
    <div className="flex flex-col gap-3">
      {rows.map((row, index) => (
        <BookingCard key={row.key} row={row} emphasis={!!emphasiseFirst && index === 0} />
      ))}
    </div>
  );
}

/**
 * Empty sections get one quiet line rather than a full card. The old empty
 * state spent roughly 250px of a phone screen saying nothing.
 */
function EmptySection({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-line bg-surface px-4 py-3.5 sm:px-[18px]">
      <p className="text-body-sm text-soft">{message}</p>
    </div>
  );
}

function SectionHeading({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex items-baseline gap-2">
      <h2 className="text-mono-section uppercase text-text-muted">{label}</h2>
      <span className="text-mono-section text-text-muted">({count})</span>
    </div>
  );
}

function SignOutButton({ token }: { token: string }) {
  const { t } = useTranslation('myBookings');
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();

  const { mutate, isPending } = useMutation({
    mutationFn: () => customerBookingsService.logout(token),
    // The link is dead either way once the user asked to leave, so a failed
    // request still drops them back on the email form.
    onSettled: () => {
      queryClient.removeQueries({ queryKey: ['customer-bookings', token] });
      navigate('/my-bookings', { replace: true });
    },
  });

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => mutate()}
      disabled={isPending}
      title={t('list.logout')}
      aria-label={t('list.logout')}
    >
      {isPending ? (
        <Loader2 className="h-4 w-4 animate-spin sm:mr-1.5" />
      ) : (
        <LogOut className="h-4 w-4 sm:mr-1.5" />
      )}
      <span className="hidden sm:inline">{t('list.logout')}</span>
    </Button>
  );
}

export default function CustomerBookingsPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation('myBookings');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['customer-bookings', token],
    queryFn: () => customerBookingsService.getByToken(token),
    enabled: !!token,
    retry: false,
  });

  if (isLoading) {
    return (
      <CustomerPageShell>
        <div className="flex min-h-[60vh] items-center justify-center p-4">
          <div className="space-y-4 text-center">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-brand-500" />
            <p className="text-soft">{t('list.loading')}</p>
          </div>
        </div>
      </CustomerPageShell>
    );
  }

  // An invalid or expired token drops the customer back onto the email form so
  // they can request a fresh link without hunting for the entry page.
  if (isError || !data) {
    return (
      <CustomerPageShell>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="mx-auto max-w-md px-4 py-10 sm:px-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarCheck className="h-5 w-5" aria-hidden="true" />
                {t('list.linkExpiredTitle')}
              </CardTitle>
              <CardDescription>{t('list.linkExpiredDescription')}</CardDescription>
            </CardHeader>
            <CardContent>
              <MagicLinkRequestForm />
            </CardContent>
          </Card>
        </div>
      </CustomerPageShell>
    );
  }

  const upcoming = data.upcoming_bookings.map((booking) => bookingToRow(booking, t, true));
  const past = data.past_bookings.map((booking) => bookingToRow(booking, t, false));
  const waitlist = data.waitlist_entries.map((entry) => waitlistToRow(entry, t));

  return (
    <CustomerPageShell actions={<SignOutButton token={token} />}>
      <Helmet>
        <title>{t('list.title')}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Reading width, not full bleed: the old max-w-6xl stretched three short
          columns across the whole screen. */}
      <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-8">
        <div>
          <h1 className="text-h1 text-ink">{t('list.title')}</h1>
          <p className="mt-1 text-body-sm text-text-muted">{data.email}</p>
        </div>

        <section className="flex flex-col gap-3">
          <SectionHeading label={t('list.upcoming')} count={upcoming.length} />
          {upcoming.length > 0 ? (
            <EntriesList rows={upcoming} emphasiseFirst />
          ) : (
            <EmptySection message={t('list.noUpcoming')} />
          )}
        </section>

        {waitlist.length > 0 && (
          <section className="flex flex-col gap-3">
            <SectionHeading label={t('list.waitlist')} count={waitlist.length} />
            <p className="text-body-sm text-soft">{t('list.waitlistDescription')}</p>
            <EntriesList rows={waitlist} />
          </section>
        )}

        <section className="flex flex-col gap-3">
          <SectionHeading label={t('list.past')} count={past.length} />
          {past.length > 0 ? (
            <EntriesList rows={past} />
          ) : (
            <EmptySection message={t('list.noPast')} />
          )}
        </section>
      </div>
    </CustomerPageShell>
  );
}
